/**
 * Bot Detection Utility Functions
 * Helper functions for analyzing and reporting bot detection results
 */

/**
 * Push comprehensive bot detection data to GTM dataLayer
 * @param {Object} result - Bot detection result from useBotDetection
 * @param {String} formType - Type of form (e.g., 'quick_apply', 'contact', 'job_application')
 */
export const pushBotDataToGTM = (result, formType) => {
  if (!result) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'form_bot_analysis',
    formType,
    isBot: result.isBot,
    botDetectionPassed: result.passed,
    botDetectionReason: result.reason,
    honeypotTriggered: result.honeypot?.isTriggered || false,
    formTimingMs: result.timing?.timeSpent || 0,
    formTimingSuspicious: result.timing?.isSuspicious || false,
    recaptchaV2Required: result.requiresV2 || false,
    recaptchaV2Completed: result.v2Completed || false,
    canSubmit: result.canSubmit
  });
};

/**
 * Get user-friendly error message based on bot detection result
 * @param {Object} result - Bot detection result
 * @returns {String} Error message to display to user
 */
export const getBotDetectionErrorMessage = (result) => {
  if (!result || result.passed) return null;

  switch (result.reason) {
    case 'honeypot_triggered':
      return 'Unable to process your submission. Please try again or contact support.';

    case 'verification_required':
    case 'v2_verification_incomplete':
      return result.message || 'Please complete the verification to continue.';

    case 'timing_suspicious':
      return 'Please take a moment to review your information before submitting.';

    default:
      return 'Unable to verify your submission. Please try again.';
  }
};

/**
 * Determine if submission should be blocked
 * @param {Object} result - Bot detection result
 * @returns {Boolean} true if submission should be blocked
 */
export const shouldBlockSubmission = (result) => {
  if (!result) return true;

  // Block if marked as bot
  if (result.isBot) return true;

  // Block if checks didn't pass
  if (!result.passed) return true;

  // Block if not verified to submit
  if (!result.canSubmit) return true;

  return false;
};

/**
 * Log bot detection result to console (for debugging)
 * @param {Object} result - Bot detection result
 * @param {String} formType - Type of form
 */
export const logBotDetection = (result, formType) => {
  if (!result) return;

  const logStyle = result.passed
    ? 'color: green; font-weight: bold;'
    : 'color: orange; font-weight: bold;';

  console.log(`%c[Bot Detection - ${formType}]`, logStyle);
  console.log('Result:', result);
  console.log('Details:', {
    passed: result.passed,
    isBot: result.isBot,
    reason: result.reason,
    honeypot: result.honeypot,
    timing: result.timing,
    v2Required: result.requiresV2,
    canSubmit: result.canSubmit
  });
};

/**
 * Generate form submission metadata including bot detection signals
 * @param {Object} result - Bot detection result
 * @returns {Object} Metadata to include with form submission
 */
export const generateSubmissionMetadata = (result) => {
  if (!result) {
    return {
      botDetection: 'not_run',
      timestamp: new Date().toISOString()
    };
  }

  return {
    botDetection: result.passed ? 'passed' : 'failed',
    botDetectionReason: result.reason,
    honeypotPassed: result.honeypot?.passed || false,
    timingPassed: result.timing?.passed || false,
    formTimingMs: result.timing?.timeSpent || 0,
    recaptchaV2Used: result.v2Completed || false,
    recaptchaV3Token: result.v3Token ? 'present' : 'missing',
    timestamp: new Date().toISOString()
  };
};

/**
 * Check if browser environment supports required features
 * @returns {Boolean} true if environment is compatible
 */
export const isEnvironmentCompatible = () => {
  // Check for required APIs
  const hasDataLayer = typeof window !== 'undefined' && window.dataLayer !== undefined;
  const hasLocalStorage = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  const hasConsole = typeof console !== 'undefined';

  return hasDataLayer || hasLocalStorage || hasConsole; // At least one should be available
};

/**
 * Get reCAPTCHA configuration status
 * @returns {Object} Configuration status
 */
export const getRecaptchaConfig = () => {
  return {
    v3Configured: !!process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY,
    v2Configured: !!process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY,
    v3SiteKey: process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY,
    v2SiteKey: process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY
  };
};

/**
 * Sanitize form data to remove bot-related fields before submission
 * @param {Object} formData - Form data object
 * @returns {Object} Sanitized form data
 */
export const sanitizeFormData = (formData) => {
  const sanitized = { ...formData };

  // Remove honeypot field
  delete sanitized.website;

  // Remove any other bot detection fields
  delete sanitized.botCheck;
  delete sanitized._gotcha;

  return sanitized;
};

const botDetectionUtils = {
  pushBotDataToGTM,
  getBotDetectionErrorMessage,
  shouldBlockSubmission,
  logBotDetection,
  generateSubmissionMetadata,
  isEnvironmentCompatible,
  getRecaptchaConfig,
  sanitizeFormData
};

export default botDetectionUtils;
